import React, { forwardRef, useState, useRef, useMemo, useEffect } from 'react';
import { getCountryCallingCode, isPossiblePhoneNumber, isValidPhoneNumber, formatPhoneNumberIntl, formatPhoneNumber } from 'react-phone-number-input';
import PhoneNumberInputStyled from './bundle.esm2.js';
import SelectCountry from './SelectCountry/bundle.esm.js';
import { defaultFunction } from '../../utils/bundle.esm2.js';
import { transformPhoneNumber } from './bundle.esm3.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const PhoneNumberInput = forwardRef((props, ref) => {
  const _a = props, {
    id,
    name,
    label,
    className,
    inputValue,
    selectValue,
    initialCountryCode,
    disabled,
    minLength,
    maxLength,
    placeholder,
    errorMessage,
    countriesCodes,
    hideCountryCode,
    onChange = defaultFunction
  } = _a, rest = __objRest(_a, [
    "id",
    "name",
    "label",
    "className",
    "inputValue",
    "selectValue",
    "initialCountryCode",
    "disabled",
    "minLength",
    "maxLength",
    "placeholder",
    "errorMessage",
    "countriesCodes",
    "hideCountryCode",
    "onChange"
  ]);
  const [, setFlagDisplayed] = useState();
  const [currentPhoneNumber, setCurrentPhoneNumber] = useState(
    () => transformPhoneNumber(inputValue != null ? inputValue : "")
  );
  const errorMessageId = `${id != null ? id : name}-error`;
  const countriesCodesIsEmpty = countriesCodes.length === 0;
  const [currentCountryCode, setCurrentCountryCode] = useState(
    initialCountryCode != null ? initialCountryCode : countriesCodes[0]
  );
  const warningByInitialCountryCodeWasThrown = useRef(false);
  const firstCountryCode = useMemo(
    () => countriesCodes.length > 0 ? countriesCodes[0] : void 0,
    [countriesCodes]
  );
  const countryCodeIsInCountriesCodeList = useMemo(
    () => countriesCodes.some((countryCode) => countryCode === initialCountryCode),
    [countriesCodes, initialCountryCode]
  );
  const renderErrorMessage = () => {
    if (!errorMessage)
      return null;
    if (typeof errorMessage === "string") {
      return /* @__PURE__ */ React.createElement("span", {
        id: errorMessageId,
        className: "PhoneNumberInput__errorMessage"
      }, errorMessage);
    }
    return /* @__PURE__ */ React.createElement("div", {
      className: "PhoneNumberInput__errorMessage"
    }, errorMessage);
  };
  const changeHandler = (inputValue2, selectValue2) => {
    const hasLetters = RegExp(/[A-Za-z]/);
    if (disabled || inputValue2 && hasLetters.test(inputValue2))
      return;
    const newSelectedCountry = selectValue2 ? selectValue2 : currentCountryCode;
    const countryCallingCode = getCountryCallingCode(newSelectedCountry);
    const newPhoneNumber = typeof inputValue2 === "string" ? inputValue2 : currentPhoneNumber;
    const phoneNumberWithCode = `+${countryCallingCode}${newPhoneNumber}`;
    onChange({
      isPossible: isPossiblePhoneNumber(phoneNumberWithCode),
      isValid: isValidPhoneNumber(phoneNumberWithCode),
      international: formatPhoneNumberIntl(phoneNumberWithCode).replace(
        /\s/g,
        ""
      ),
      national: newPhoneNumber.replace(/ /g, ""),
      internationalRaw: formatPhoneNumberIntl(phoneNumberWithCode),
      nationalRaw: formatPhoneNumber(phoneNumberWithCode),
      countryCode: newSelectedCountry,
      countryCallingCode: `+${countryCallingCode}`
    });
    setCurrentPhoneNumber(transformPhoneNumber(newPhoneNumber));
  };
  const selectChangeHandler = (value) => {
    if (typeof value === "undefined" || disabled)
      return;
    setCurrentCountryCode(value);
    changeHandler(void 0, value);
    setFlagDisplayed(currentCountryCode);
  };
  const renderLabel = () => {
    if (!label)
      return null;
    return /* @__PURE__ */ React.createElement("label", {
      className: "PhoneNumberInput__label",
      htmlFor: id != null ? id : name
    }, label);
  };
  const renderSelectCountry = () => {
    if (hideCountryCode)
      return null;
    return /* @__PURE__ */ React.createElement(SelectCountry, {
      className: "PhoneNumberInput__select",
      disabled,
      options: countriesCodes,
      value: currentCountryCode,
      onChange: (event) => selectChangeHandler(event.target.value)
    });
  };
  useEffect(() => {
    if (name || !label)
      return;
    console.warn(
      "PhoneNumberInputUI: When using the label tag consider passing the 'name' prop"
    );
  }, [name, label]);
  useEffect(() => {
    if (name || id || !errorMessage)
      return;
    console.warn(
      "PhoneNumberInputUI: If you want to display an error consider passing the 'name' prop or 'id' prop or both"
    );
  }, [name, id, errorMessage]);
  useEffect(() => {
    if (countryCodeIsInCountriesCodeList || warningByInitialCountryCodeWasThrown.current || countriesCodesIsEmpty || !firstCountryCode || !initialCountryCode) {
      return;
    }
    console.warn(
      `PhoneNumberInputUI: The initial value for the country code dropdown "${initialCountryCode}" is not in the list of codes of the countriesCodes prop. Will be using "${firstCountryCode}" as the initial value for the prop initialCountryCode.`
    );
    warningByInitialCountryCodeWasThrown.current = true;
    setCurrentCountryCode(firstCountryCode);
    setFlagDisplayed(firstCountryCode);
  }, [
    countriesCodesIsEmpty,
    countryCodeIsInCountriesCodeList,
    firstCountryCode,
    initialCountryCode
  ]);
  if (countriesCodesIsEmpty)
    return null;
  return /* @__PURE__ */ React.createElement(PhoneNumberInputStyled, {
    className: `PhoneNumberInput ${className}`
  }, renderLabel(), /* @__PURE__ */ React.createElement("div", {
    className: "PhoneNumberInput__select-and-input"
  }, renderSelectCountry(), /* @__PURE__ */ React.createElement("input", __spreadProps(__spreadValues({
    id: id != null ? id : name,
    type: "tel",
    inputMode: "numeric",
    className: "PhoneNumberInput__input",
    name,
    disabled,
    placeholder,
    maxLength,
    minLength,
    "aria-invalid": errorMessage ? "true" : "false",
    "aria-errormessage": errorMessageId
  }, rest), {
    value: currentPhoneNumber,
    onChange: (e) => changeHandler(e.target.value),
    ref
  }))), renderErrorMessage());
});
PhoneNumberInput.defaultProps = {
  className: "",
  maxLength: 20,
  minLength: 4
};

export { PhoneNumberInput as default };
//# sourceMappingURL=bundle.esm.js.map
